import React, { useEffect, useMemo, useState } from 'react';
import { fetchBreakStatus } from '../../services/attendanceService';
import { subscribeAttendanceSessionChanged } from '../../services/attendanceSessionBus';
import { WORK_WATCH_MAX_SECONDS } from '../../utils/workSessionUtils';

const formatWatch = (totalSeconds) => {
  const secs = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export const liveElapsedSeconds = (status) => {
  if (!status) return 0;
  const accumulated = Number(status.workAccumulatedSeconds) || 0;
  let elapsed = Number(status.workElapsedSeconds) || accumulated;
  if (status.workRunning && status.workRunningSince) {
    const startedMs = Date.parse(status.workRunningSince);
    if (!Number.isNaN(startedMs)) {
      elapsed = accumulated + Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
    }
  }
  return Math.min(WORK_WATCH_MAX_SECONDS, Math.max(0, elapsed));
};

/** Live office/work minutes from break-status payload (matches the work timer). */
export const liveOfficeMinutes = (status) => Math.floor(liveElapsedSeconds(status) / 60);

/** True while checked in and not logged out (timer still ticking). */
export const isActiveWorkSession = (status) => Boolean(status?.checkedIn && !status?.loggedOut);

/**
 * Office minutes for productivity: live timer while session is open;
 * after logout / 16h auto-close, use persisted day timeline minutes.
 */
export const resolveOfficeMinutesForProductivity = ({
  breakStatus,
  dayOfficeMinutes = 0,
  preferLiveTimer = true,
} = {}) => {
  const liveMins = liveOfficeMinutes(breakStatus);
  const storedMins = Math.max(0, Number(dayOfficeMinutes) || 0);
  if (preferLiveTimer && isActiveWorkSession(breakStatus)) {
    return liveMins;
  }
  return Math.max(storedMins, liveMins);
};

/**
 * Productivity = completed work minutes / office timer minutes.
 * e.g. 30 completed / 120 office = 25%.
 */
export const calcOfficeProductivityPercent = (completedMinutes, officeMinutes) => {
  const done = Math.max(0, Number(completedMinutes) || 0);
  const office = Math.max(0, Number(officeMinutes) || 0);
  if (office <= 0) return 0;
  return Math.min(100, Math.round((done / office) * 100));
};

/** Hook: live office minutes that tick while the work timer is running. */
export const useLiveOfficeMinutes = (enabled = true) => {
  const [status, setStatus] = useState(null);
  const [nowTick, setNowTick] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setStatus(null);
      return undefined;
    }
    let cancelled = false;

    const refresh = async () => {
      try {
        const next = await fetchBreakStatus();
        if (!cancelled) setStatus(next);
      } catch {
        if (!cancelled) setStatus(null);
      }
    };

    refresh();
    const unsubscribe = subscribeAttendanceSessionChanged((detail) => {
      if (detail?.status && typeof detail.status.workElapsedSeconds === 'number') {
        setStatus(detail.status);
      }
      refresh();
    });
    const poll = window.setInterval(refresh, 30000);
    return () => {
      cancelled = true;
      unsubscribe();
      window.clearInterval(poll);
    };
  }, [enabled]);

  const elapsed = useMemo(
    () => liveElapsedSeconds(status),
    [status, nowTick],
  );
  const atCap = elapsed >= WORK_WATCH_MAX_SECONDS;
  const running = Boolean(status?.workRunning) && !atCap;

  useEffect(() => {
    if (!enabled || !running) return undefined;
    const id = window.setInterval(() => setNowTick((tick) => tick + 1), 1000);
    return () => window.clearInterval(id);
  }, [enabled, running]);

  return {
    status,
    elapsedSeconds: elapsed,
    officeMinutes: Math.floor(elapsed / 60),
    running,
  };
};

const WorkSessionWatch = ({ enabled = true }) => {
  const { status, elapsedSeconds: elapsed, running } = useLiveOfficeMinutes(enabled);
  const atCap = elapsed >= WORK_WATCH_MAX_SECONDS;

  if (!enabled) return null;

  const checkedIn = Boolean(status?.checkedIn);
  const loggedOut = Boolean(status?.loggedOut);
  const onBreak = Boolean(status?.onBreak);

  let label = 'Not logged in';
  let tone = 'idle';
  if (loggedOut) {
    label = 'Logged out';
    tone = 'stopped';
  } else if (onBreak && !atCap) {
    label = 'On break';
    tone = 'paused';
  } else if (atCap) {
    label = 'Stopped';
    tone = 'stopped';
  } else if (running || checkedIn) {
    label = 'Working';
    tone = 'running';
  }

  const toneClass = {
    running: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    paused: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    stopped: 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600',
    idle: 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700',
  }[tone];

  const showLabel = tone === 'paused' || (tone === 'stopped' && loggedOut);

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 flex-shrink-0 ${toneClass}`}
      title={`${label} · pauses on break, stops at logout or 16 hours after login`}
      aria-label={`Work time ${formatWatch(elapsed)}, ${label}`}
    >
      <span
        className={`inline-block w-2 h-2 rounded-full ${
          tone === 'running' ? 'bg-emerald-500 animate-pulse' : tone === 'paused' ? 'bg-amber-500' : 'bg-gray-400'
        }`}
      />
      <span className="font-mono text-sm font-semibold tabular-nums leading-none">
        {formatWatch(elapsed)}
      </span>
      {showLabel && (
        <span className="text-xs font-medium leading-none hidden sm:inline">
          {label}
        </span>
      )}
    </div>
  );
};

export default WorkSessionWatch;
